// CLI 0.9.3 — bare-slug resolution across every org you're a member of.
//
// ADR 036 §V2 made entities globally addressable by membership: a UUID or an
// `org/slug` ref resolves in the entity's home org without any active-org
// context. A *bare* slug (no `org/` prefix, no UUID) still resolved only in
// the CLI's active org, so `cloudgrid clone widget` failed with a "not in
// your active org" error whenever `widget` lived in a different org of yours —
// even though you're a member of that org and could address it the long way.
//
// This resolver closes that gap. Given a bare slug it enumerates the user's
// orgs (`GET /api/v2/grids`) and asks each one for an entity with that slug via
// the globally-addressable `org/slug` form (`GET /api/v2/runtimes/<org>/<slug>`,
// resolved server-side by `requireEntityAccess`). A unique hit resolves the
// command with no active-org round-trip; multiple hits raise a typed
// disambiguation error naming the candidates; zero hits return null so the
// caller can word its own "not found" line.
//
// The active-org header the client carries is irrelevant here: the `org/slug`
// form is resolved by the entity's home org, not the header, so a stale or
// mismatched active org cannot block resolution.

import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { isEntityUuid, parseOrgSlugRef } from './entity-ref.js';
import { CloudGridError } from '../utils.js';

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** The runtime-detail fields this resolver reads off `GET /api/v2/runtimes/:id`. */
interface RuntimeDetailLite {
  entity_id?: string;
  name?: string;
  kind?: string;
  org?: { org_id?: string; slug?: string };
  updated_at?: string | null;
}

/**
 * A single entity that matched the bare slug, with everything the
 * disambiguation message and the calling verb need.
 */
export interface BareSlugMatch {
  entity_id: string;
  /** The entity's home org slug (source of truth: the detail response). */
  org_slug: string;
  /** The entity's home org id. */
  org_id: string;
  /** The entity's canonical slug/name. */
  entity_slug: string;
  kind: string;
  /** Last-updated timestamp, used only to annotate the disambiguation list. */
  updated_at: string | null;
}

/**
 * The 4-field shape a verb uses to address a uniquely-resolved entity. A
 * subset of {@link BareSlugMatch} — verbs re-fetch full detail by `entity_id`
 * (a globally-addressable UUID) when they need more.
 */
export interface ResolvedBareSlug {
  entity_id: string;
  home_org_slug: string;
  home_org_id: string;
  kind: string;
}

/**
 * Thrown when a bare slug matches an entity in more than one of the user's
 * orgs. The message is the §23-voice disambiguation block, so the standard
 * error printer surfaces it verbatim; `matches` is retained for callers that
 * want to render their own UI.
 */
export class MultipleEntityMatchesError extends CloudGridError {
  readonly matches: BareSlugMatch[];
  constructor(slug: string, matches: BareSlugMatch[]) {
    super(formatMultipleMatches(slug, matches));
    this.name = 'MultipleEntityMatchesError';
    this.matches = matches;
  }
}

/**
 * Resolve a bare entity slug against every org the caller belongs to.
 *
 * Returns the unique match, throws {@link MultipleEntityMatchesError} when more
 * than one org has an entity with this slug, and returns `null` when no org
 * does. Throws a {@link CloudGridError} when the input is not a bare slug
 * (a UUID, an `org/slug` ref, or a non-slug string) — those forms are
 * globally addressable or invalid and must be handled upstream.
 */
export async function resolveBareSlugAcrossOrgs(
  slug: string,
  client: IV2Client,
): Promise<ResolvedBareSlug | null> {
  const bare = slug.trim();

  // Defensive — the caller is expected to have routed UUIDs and `org/slug`
  // refs down their own (globally-addressable) path before reaching here.
  if (isEntityUuid(bare)) {
    throw new CloudGridError(
      'resolveBareSlugAcrossOrgs received a UUID; UUIDs are globally addressable and must be resolved upstream.',
    );
  }
  if (bare.includes('/') || parseOrgSlugRef(bare) !== null) {
    throw new CloudGridError(
      'resolveBareSlugAcrossOrgs received an org/slug ref; that form is resolved upstream.',
    );
  }
  if (!SLUG_RE.test(bare)) {
    throw new CloudGridError(`'${bare}' is not a valid entity slug.`);
  }

  const orgs = await client.getOrgs();

  // Fan out one lookup per org. The `org/slug` form is one encoded path
  // segment so the slash never splits the route. A 404 means "not in this
  // org" — expected and skipped; anything else is a real error and propagates.
  const results = await Promise.all(
    orgs.map(async (org): Promise<BareSlugMatch | null> => {
      try {
        const detail = await client.get<RuntimeDetailLite>(
          `/api/v2/runtimes/${encodeURIComponent(`${org.slug}/${bare}`)}`,
        );
        if (!detail?.entity_id) return null;
        return {
          entity_id: detail.entity_id,
          org_slug: detail.org?.slug ?? org.slug,
          org_id: detail.org?.org_id ?? org.org_id,
          entity_slug: detail.name ?? bare,
          kind: detail.kind ?? 'app',
          updated_at: detail.updated_at ?? null,
        };
      } catch (err) {
        if (err instanceof V2ApiError && err.status === 404) return null;
        throw err;
      }
    }),
  );

  const matches = results.filter((m): m is BareSlugMatch => m !== null);
  if (matches.length === 0) return null;
  if (matches.length === 1) {
    const m = matches[0];
    return {
      entity_id: m.entity_id,
      home_org_slug: m.org_slug,
      home_org_id: m.org_id,
      kind: m.kind,
    };
  }
  throw new MultipleEntityMatchesError(bare, matches);
}

/**
 * §23-voice "not found in any grid" line. Verb-aware so the suggested
 * `grid/slug` form names the command the user actually ran.
 */
export function bareSlugNotFoundMessage(slug: string, verb: string): string {
  return (
    `Entity '${slug}' not found in any of your grids. ` +
    `Check the spelling, or try 'cloudgrid ${verb} grid/${slug}' if you know the grid.`
  );
}

// ── disambiguation rendering ───────────────────────────────────────────────

function formatMultipleMatches(slug: string, matches: BareSlugMatch[]): string {
  const refs = matches.map((m) => `${m.org_slug}/${m.entity_slug}`);
  const width = Math.max(...refs.map((r) => r.length));
  const lines = matches.map(
    (m, i) => `  ${refs[i].padEnd(width)}  (${relativeTime(m.updated_at)})`,
  );
  return [
    `Multiple entities named '${slug}':`,
    ...lines,
    "Use the full 'grid/slug' form, or pass --grid <slug>.",
  ].join('\n');
}

/** Coarse relative-time label for the disambiguation list. */
function relativeTime(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'unknown';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
